import { Post, PostActions, Topic, Tag } from 'discourse-js';

export interface PostWithLikes extends Post {
  canLike: boolean;
  liked: boolean;
  totalLikes: number;
}

export interface PostsWithReplies extends PostWithLikes {
  replies: PostsWithReplies[];
}

interface TopicModelConstructor {
  topic: Partial<Topic> | null;
}

class TopicModel {
  _topic: Partial<Topic> | null;

  constructor({ topic }: TopicModelConstructor) {
    this._topic = topic;
  }

  getTopic = () => this._topic;

  getPosts = (): PostWithLikes[] => {
    const topic = this.getTopic();
    const posts = topic?.postStream?.posts;

    return (
      posts?.map((p) => {
        const likeAction = p.actionsSummary.find(
          ({ id }) => id === PostActions.Like
        );

        let liked = Boolean(likeAction?.acted);
        let canLike = liked || Boolean(likeAction?.canAct);
        let totalLikes = likeAction?.count || 0;

        return { ...p, canLike, liked, totalLikes };
      }) || []
    );
  };

  getFirstPost = () => {
    return this.getPosts().find((p) => p.postNumber === 1);
  };

  getTitle = () => {
    const topic = this.getTopic();
    // Only set post title if it has no "unit" or "module" tags, unless it also has a "response" tag –
    // posts with this combination of tags have auto-generated titles which we don't want to display
    let title = null;
    let unitTag = null;
    let moduleTag = null;
    let isResponse = false;

    if (topic?.tags && topic.tags.length > 0) {
      const tagList = topic.tags.map((t: Tag | string) =>
        typeof t === 'string' ? t : t.name
      );

      unitTag = tagList.find((t) => Boolean(t.match(/unit/g)));
      moduleTag = tagList.find((t: string) => Boolean(t.match(/module/g)));
      isResponse = Boolean(tagList.find((t: string) => !!t.match(/response/g)));
    }

    // If the "unit" and "module" tags are present AND the "response" tag is NOT present,
    // then the title will have been automatically generated and so is not suitable to show
    if ((!unitTag && !moduleTag) || isResponse) {
      title = topic?.title;
    } else {
      // TODO: Get session title using unit & module tags & append to post title
      const matchedUnit = unitTag && unitTag.match(/\d+/g);
      const unitNumber = matchedUnit?.length && Number(matchedUnit[0]);

      const matchedModule = moduleTag && moduleTag.match(/\d+/g);
      const moduleNumber = matchedModule?.length && Number(matchedModule[0]);

      title = `${unitNumber ? `Unit ${unitNumber}` : ''}${
        unitNumber && moduleNumber ? ', ' : ''
      }${moduleNumber ? `Session ${moduleNumber}` : ''}`;
    }

    return title;
  };

  /**
   * Returns posts formatted in a nested structure
   * with each post getting a list of replies
   *
   * Discourse gives us a list of posts for each topic, which look like this:
   * [
   *     { id: 1, number: 1 },
   *     { id: 2, number: 2 },
   *     { id: 3, number: 3, replyToPostNumber: 1 },
   *     { id: 4, number: 4, replyToPostNumber: 3 },
   * ];
   *
   * In this example, post number 3 is a reply to post number 1
   * while post number 4 is a reply to post number 3
   *
   * getNestedReplies transforms this structure, and would look like this:
   * [
   *     {
   *       id: 1,
   *       number: 1,
   *       replies: [
   *         {
   *            id: 3,
   *            number: 3,
   *            replyToPostNumber: 1,
   *            replies: [
   *               { id: 4, number: 4, replyToPostNumber: 3 },
   *            ]
   *         },
   *       ]
   *     },
   *     { id: 2, number: 2 },
   * ]
   *
   * This is allows the TopicComments component to use this data and render recursively
   */
  getNestedReplies = (
    posts?: PostWithLikes[],
    replies: PostWithLikes[] = []
  ): PostsWithReplies[] => {
    if (!posts) {
      // Ignoring the very first post - this is not a reply
      // (it's the first post for this topic)
      const allPosts = this.getPosts().filter((p) => p.postNumber !== 1);

      // Separate all posts into 2 lists:
      // - originals : replies to the first post and posts which aren't replies
      // - replies : replies to originals
      const originals = allPosts.filter(
        (p) => !Boolean(p.replyToPostNumber) || p.replyToPostNumber === 1
      );
      const replyPosts = allPosts.filter(
        (p) => Boolean(p.replyToPostNumber) && p.replyToPostNumber !== 1
      );

      return this.getNestedReplies(originals, replyPosts);
    }

    if (!posts.length) return [];

    return posts.map((p) => ({
      ...p,
      replies: this.getNestedReplies(
        replies.filter((r) => r.replyToPostNumber === p.postNumber),
        replies
      ),
    }));
  };
}

export default TopicModel;
